import React, { useState, useEffect, useMemo } from 'react';
import Header from '../components/Header';
import FilterBar, { FilterSummary } from '../components/FilterBar';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { visionZeroData } from '../data/visionZeroData';
import './VisionZero.css';
import { usePersistentFlag, usePersistentSet } from '../utils/expandedState';

const TIMELINES = [
  { value: 'Short-term', label: 'Short-term' },
  { value: 'Near-term', label: 'Near-term' },
  { value: 'Long-term', label: 'Long-term' },
  { value: 'Ongoing', label: 'Ongoing' },
];

// Curated recurring departmental leads used for the Responsible filter. One-off
// parties (Transportation Board, advisory committees, Purchasing, Town Counsel,
// Longwood TMA) still appear as tags on each strategy.
const RESPONSIBLES = [
  { value: 'DPW Engineering & Transportation Division', label: 'DPW Engineering & Transportation' },
  { value: 'DPW Highway Division', label: 'DPW Highway Division' },
  { value: 'Brookline Police Department', label: 'Police Department' },
  { value: 'Planning Department', label: 'Planning Department' },
  { value: 'Communications Officer', label: 'Communications Officer' },
];

function getAllStrategies(data) {
  const strategies = [];
  data.themes.forEach(theme => {
    theme.strategies.forEach(s => strategies.push(s));
  });
  return strategies;
}

function timelineClass(timeline) {
  if (timeline === 'Short-term') return 'vz-timeline-short';
  if (timeline === 'Near-term') return 'vz-timeline-near';
  if (timeline === 'Long-term') return 'vz-timeline-long';
  if (timeline === 'Ongoing') return 'vz-timeline-ongoing';
  return '';
}

function VisionZero() {
  const [sel, setSel] = useState({ timeline: new Set(), responsible: new Set() });
  const [expandedThemes, setExpandedThemes] = usePersistentSet('vision-zero:expandedThemes', () => new Set(visionZeroData.themes.map(t => t.id)));
  const [expandedStrategies, setExpandedStrategies] = usePersistentSet('vision-zero:expandedStrategies');
  const [overviewExpanded, setOverviewExpanded] = usePersistentFlag('vision-zero:overviewExpanded');
  const [strategiesNoteExpanded, setStrategiesNoteExpanded] = usePersistentFlag('vision-zero:strategiesNoteExpanded');

  useEffect(() => {
    document.title = 'Vision Zero Action Plan';
  }, []);

  const allStrategies = useMemo(() => getAllStrategies(visionZeroData), []);

  const dimensions = useMemo(() => [
    { key: 'timeline', label: 'Timeline', values: TIMELINES.map(t => ({
      value: t.value, label: t.label,
      count: allStrategies.filter(s => s.timeline === t.value).length,
    })) },
    { key: 'responsible', label: 'Responsible', values: RESPONSIBLES.map(r => ({
      value: r.value, label: r.label,
      count: allStrategies.filter(s => s.responsibleParties && s.responsibleParties.includes(r.value)).length,
    })) },
  ], [allStrategies]);

  const toggleValue = (key, value) => setSel(prev => {
    const next = new Set(prev[key]);
    if (next.has(value)) next.delete(value); else next.add(value);
    return { ...prev, [key]: next };
  });
  const clearDimension = (key) => setSel(prev => ({ ...prev, [key]: new Set() }));
  const clearAllFilters = () => setSel({ timeline: new Set(), responsible: new Set() });

  const matchesStrategy = useMemo(() => {
    return (s) => {
      if (sel.timeline.size && !sel.timeline.has(s.timeline)) return false;
      if (sel.responsible.size && !(s.responsibleParties && [...sel.responsible].some(r => s.responsibleParties.includes(r)))) return false;
      return true;
    };
  }, [sel]);

  const filteredThemes = useMemo(() => {
    return visionZeroData.themes.map(theme => ({
      ...theme,
      strategies: theme.strategies.filter(matchesStrategy)
    })).filter(theme => theme.strategies.length > 0);
  }, [matchesStrategy]);

  const totalFiltered = useMemo(() => {
    return filteredThemes.reduce((sum, theme) => sum + theme.strategies.length, 0);
  }, [filteredThemes]);

  const toggleTheme = (themeId) => {
    setExpandedThemes(prev => {
      const next = new Set(prev);
      if (next.has(themeId)) next.delete(themeId);
      else next.add(themeId);
      return next;
    });
  };

  const toggleStrategy = (strategyId) => {
    setExpandedStrategies(prev => {
      const next = new Set(prev);
      if (next.has(strategyId)) next.delete(strategyId);
      else next.add(strategyId);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedThemes(new Set(visionZeroData.themes.map(t => t.id)));
    setExpandedStrategies(new Set(allStrategies.map(s => s.id)));
    setOverviewExpanded(true);
    setStrategiesNoteExpanded(true);
  };

  const collapseAll = () => {
    setExpandedThemes(new Set());
    setExpandedStrategies(new Set());
    setOverviewExpanded(false);
    setStrategiesNoteExpanded(false);
  };

  const renderStrategyMetadata = (s) => (
    <div className="vz-strategy-metadata">
      <div className="vz-meta-row">
        <span className={`vz-timeline-badge ${timelineClass(s.timeline)}`}>{s.timeline}</span>
        {s.responsibleParties && s.responsibleParties.length > 0 && (
          <span className="vz-meta-item">
            <span className="vz-meta-label">Responsible:</span>{' '}
            {s.responsibleParties.join(' · ')}
          </span>
        )}
      </div>
      {s.resources && s.resources.length > 0 && (
        <div className="vz-resources">
          <span className="vz-meta-label">Resources &amp; precedents:</span>
          <ul className="vz-resources-list">
            {s.resources.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <div className="vz-page">
      <a href="#main-content" className="vz-skip-link">Skip to main content</a>
      <Header
        title="Vision Zero Action Plan"
        creditExtra="website design by Michael Rubenstein"
      />

      <main id="main-content" tabIndex={-1} className="vz-content">
        <div className="vz-intro">
          <p className="vz-updated">
            Town of Brookline · Adopted June {visionZeroData.planYear} · Goal: Vision Zero by {visionZeroData.goalYear}
          </p>
        </div>

        <div className="vz-controls">
          <FilterBar
            allCount={allStrategies.length}
            dimensions={dimensions}
            selected={sel}
            onToggleValue={toggleValue}
            onClearDimension={clearDimension}
            onClearAll={clearAllFilters}
            trailing={
              <>
                <button className="vz-expand-btn" onClick={expandAll}>Expand All</button>
                <button className="vz-expand-btn" onClick={collapseAll}>Collapse All</button>
              </>
            }
          />
        </div>

        <FilterSummary
          count={totalFiltered}
          noun="strategy"
          nounPlural="strategies"
          dimensions={dimensions}
          selected={sel}
          onClearAll={clearAllFilters}
        />

        <div className="vz-preamble-sections">
          <div className="vz-preamble">
            <button
              className={`vz-preamble-header ${overviewExpanded ? 'expanded' : ''}`}
              onClick={() => setOverviewExpanded(!overviewExpanded)}
              aria-expanded={overviewExpanded}
            >
              <span className={`vz-chevron${overviewExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="vz-preamble-title">About This Plan</span>
            </button>
            {overviewExpanded && (
              <div className="vz-preamble-body">
                {visionZeroData.introduction.overview.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </div>

          <div className="vz-preamble">
            <button
              className={`vz-preamble-header ${strategiesNoteExpanded ? 'expanded' : ''}`}
              onClick={() => setStrategiesNoteExpanded(!strategiesNoteExpanded)}
              aria-expanded={strategiesNoteExpanded}
            >
              <span className={`vz-chevron${strategiesNoteExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="vz-preamble-title">About the Strategies &amp; Timeline Key</span>
            </button>
            {strategiesNoteExpanded && (
              <div className="vz-preamble-body">
                {visionZeroData.introduction.strategiesNote.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
                <ul className="vz-timeline-key">
                  {visionZeroData.legend.timeline.map(t => (
                    <li key={t.code}>
                      <span className={`vz-timeline-badge ${timelineClass(t.code)}`}>{t.code}</span>
                      <span className="vz-timeline-key-label">{t.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="vz-themes">
          {filteredThemes.map(theme => (
            <div key={theme.id} className="vz-theme">
              <h2 className="vz-heading"><button
                className={`vz-theme-header ${expandedThemes.has(theme.id) ? 'expanded' : ''}`}
                onClick={() => toggleTheme(theme.id)}
                aria-expanded={expandedThemes.has(theme.id)}
              >
                <span className={`vz-chevron vz-chevron-light${expandedThemes.has(theme.id) ? ' expanded' : ''}`} aria-hidden="true"></span>
                <div className="vz-theme-header-text">
                  <span className="vz-theme-name">{theme.number}. {theme.name}</span>
                  {theme.description && (
                    <span className="vz-theme-desc">{theme.description}</span>
                  )}
                </div>
                <span className="vz-theme-count" aria-hidden="true">{theme.strategies.length}</span>
              </button></h2>

              {expandedThemes.has(theme.id) && (
                <div className="vz-theme-body">
                  {theme.strategies.map(s => {
                    const isExpanded = expandedStrategies.has(s.id);
                    return (
                      <div key={s.id} className="vz-strategy">
                        <h3 className="vz-heading"><button
                          className={`vz-strategy-header ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleStrategy(s.id)}
                          aria-expanded={isExpanded}
                        >
                          <span className={`vz-chevron${isExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
                          <span className="vz-strategy-id">{s.id}</span>
                          <span className="vz-strategy-title">{s.title}</span>
                          <span className={`vz-timeline-badge small ${timelineClass(s.timeline)}`}>{s.timeline}</span>
                        </button></h3>
                        {isExpanded && (
                          <div className="vz-strategy-body">
                            {renderStrategyMetadata(s)}
                            {s.description && (
                              <p className="vz-strategy-desc">{s.description}</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="vz-source">
          <p>
            Source: Brookline Vision Zero Action Plan (FINAL, June {visionZeroData.planYear}), Town of Brookline
            Department of Public Works, Engineering &amp; Transportation Division; prepared by Toole Design.
            This page presents the plan’s 28 strategies (six themes); it omits the Systemic Safety Toolbox, crash
            analysis, implementation strategy, and appendices.
          </p>
        </div>

        <DisclaimerFooter>
          <p>
            For source information from the Town of Brookline, go to the{' '}
            <a href="https://www.brooklinema.gov/3783/Vision-Zero-Action-Plan" target="_blank" rel="noopener noreferrer">
              Vision Zero Action Plan<span className="visually-hidden"> (opens in new tab)</span>
            </a>
            {' '}web page on the Town of Brookline website.
          </p>
        </DisclaimerFooter>
      </main>
    </div>
  );
}

export default VisionZero;
